import { Component, Element, Prop, VNode, Watch, h } from '@stencil/core';

import * as Pipeline from '../core/utils/pipeline';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { Dictionary } from '../core/classes/Dictionary';
import { FEEDBACK_MESSAGE } from './ads-loader.types';
import { StandardSize, StandardSizeType, Theme } from '../core/types/globalTypes';
import { generateId } from '../core/utils/components';
import { isNullOrUndefinedOrEmpty } from 'utils/collection';

/**
 * Loaders are animated images that notify to users that loading is underway when applications are retrieving data or performing slow computations. Although it may not be obvious what is occurring on the back end, we can communicate clearly to reassure the user that progress is happening.
 * @tag `<ads-loader>`
 * @example <ads-loader></ads-loader>
 */
@Component({
  tag: 'ads-loader',
  styleUrl: 'ads-loader.scss',
  shadow: true,
})
export class AdsLoader implements BaseComponent {
  /**
   * @hidden
   */
  public component: string;

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-loader'));
    this.component = 'ads-loader';
    Dictionary.registerComponent(this);
  }

  @Element() private el: HTMLAdsLoaderElement;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * The theme for this component instance (optional)
   * Values: "default", "dark"
   */
  @Prop({ reflect: true }) theme?: Theme;

  /**
   * The feedback message (optional)
   */
  @Prop({ reflect: true }) label?: string;

  /**
   * Sets the size of the loader.
   * The size can be "xs", "sm", "md", "lg", or "xl". (optional)
   * @default "md"
   */
  @Prop({ reflect: true }) size: StandardSizeType = StandardSize.MEDIUM;

  /**
   * Text to display as the aria-label on the loader
   * @default "loading"
   */
  @Prop({ reflect: true }) srLabel: string = Dictionary.get(FEEDBACK_MESSAGE, 'LOADING');

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-loader'));
  }

  protected componentDidLoad(): void {
    Pipeline.addComponent(this.el);
  }

  protected disconnectedCallback(): void {
    Pipeline.removeComponent(this.el);
  }

  protected render(): VNode {
    const attributes = {
      'data-size': this.size,
      role: 'alert',
      'aria-label': this.srLabel,
    };

    return (
      <div class="ads-loader" {...attributes}>
        <div class="dots">
          <div class="dot" />
          <div class="dot" />
          <div class="dot" />
        </div>
        {!isNullOrUndefinedOrEmpty(this.label) ? (
          <div class="text" aria-hidden="true">
            {this.label}
          </div>
        ) : null}
      </div>
    ) as VNode;
  }
}
